import { useMemo } from 'react';

import { useClient } from 'urql';
import { fromPromise } from 'xstate5';

import { OrderTypeValues } from '@sg/shared-be/features/ordering';
import {
  type FetchPaymentStateParams,
  type FetchPaymentStateResponse,
} from '@sg/shared-fe/features/ordering';

import {
  GiftCardPaymentStateDocument,
  type GiftCardPaymentStateQuery,
} from '../../graphql/GiftCardPaymentState.generated';

export const useGiftCardPaymentState = () => {
  const client = useClient();

  const fetchGiftCardPaymentState = useMemo(
    () =>
      fromPromise<FetchPaymentStateResponse, FetchPaymentStateParams>(async ({ input }) => {
        const orderId = input?.orderId;

        const query = client.query<GiftCardPaymentStateQuery>;

        if (!orderId) throw new Error('Order ID is required');

        const response = await query(
          GiftCardPaymentStateDocument,
          { orderId, orderType },
          { requestPolicy },
        ).toPromise();

        const paymentState = response.data?.orderPaymentState;
        if (!paymentState) throw new Error('Gift card payment state not found');

        const maybeUpdatedAt = Number(paymentState.updatedAt);

        const paymentIntentId = paymentState.paymentIntentId;
        const paymentStateExpiration = Number.isNaN(maybeUpdatedAt) ? undefined : maybeUpdatedAt;
        const paymentStateStatus = paymentState.status;
        const paymentStateError = paymentState.error;

        return {
          orderId,
          paymentIntentId,
          paymentStateExpiration,
          paymentStateStatus,
          paymentStateError,
        };
      }),
    [client.query],
  );

  return { fetchGiftCardPaymentState };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const requestPolicy = 'network-only' as const;
const orderType = OrderTypeValues.GiftCardOrder;
